/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  CardHeader,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  FormGroup,
  InputGroup,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';
import { api } from '../../config';
import UserFormModal from './component/UserFormModal';

const UserDetailPage = () => {
  const [ids] = useState(useParams().id);
  const token = useSelector(state => state.Login?.user?.token);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const lockStatus = useMemo(() => {
    if (!user || !user.lockoutEnd) {
      return { isLocked: false, isPermanent: false };
    }
    const lockoutDate = new Date(user.lockoutEnd);
    const now = new Date();
    const isLocked = lockoutDate > now;
    const isPermanent = isLocked && lockoutDate.getFullYear() > 2100;
    return { isLocked, isPermanent };
  }, [user]);

  const fetchUserDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${api.API_URL}/appuser/${ids}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      console.log('Raw API response:', res);
      console.log('User data extracted:', res.data?.data || res.data);

      setUser(res);
    } catch (err) {
      console.error('fetchUserDetail error:', err);
      const errorMsg =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to fetch user details';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        setError('Session expired. Please login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [ids, token]);

  const handleEditSuccess = useCallback(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  useEffect(() => {
    if (ids) fetchUserDetail();
  }, [fetchUserDetail]);

  const handleLockUnlock = async action => {
    const isLock = action === 'lock';
    const result = await Swal.fire({
      title: isLock ? 'Lock User Account?' : 'Unlock User Account?',
      text: isLock
        ? 'The user will not be able to sign in until manually unlocked.'
        : 'The user will regain access to the system immediately.',
      icon: isLock ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor: isLock ? '#d33' : '#198754',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, ${action} it!`,
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      try {
        let body = {};
        if (isLock) {
          const expiryDate = new Date();
          expiryDate.setFullYear(expiryDate.getFullYear() + 100);
          body = { lockoutEnd: expiryDate.toISOString() };
        }

        const res = await axios.post(
          `${api.API_URL}/appuser/${ids}/${action}`,
          body,
          {
            withXSRFToken: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
          }
        );

        if (res.status === 200 || res.data?.success) {
          Swal.fire('Success', `User has been ${action}ed.`, 'success');
          fetchUserDetail();
        } else {
          Swal.fire(
            'Error',
            res.data?.message || `Failed to ${action}`,
            'error'
          );
        }
      } catch (e) {
        Swal.fire('Error', 'Network error occurred', 'error');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleChangePassword = async () => {
    const currentPassword = passwordForm.currentPassword || '';
    const newPassword = passwordForm.newPassword || '';
    const errors = {};

    if (!currentPassword)
      errors.currentPassword = 'Current Password is required';

    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasNonAlphanumeric = /[^A-Za-z0-9]/.test(newPassword);

    if (!newPassword) errors.newPassword = 'New Password is required';
    else if (newPassword.length < 8)
      errors.newPassword = 'Minimum 8 characters required';
    else if (!hasUppercase)
      errors.newPassword = 'Must have at least one uppercase (A-Z)';
    else if (!hasNonAlphanumeric)
      errors.newPassword = 'Must have at least one symbol (!@#$)';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${api.API_URL}/appuser/${ids}/change-password`,
        {
          userId: ids,
          currentPassword: currentPassword,
          newPassword: newPassword,
        },
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (res.status === 200 || res.data?.success) {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '' });
        setFormErrors({});
        Swal.fire('Success', 'Password changed successfully', 'success');
      } else {
        Swal.fire(
          'Error',
          res.data?.message || 'Failed to change password',
          'error'
        );
      }
    } catch (e) {
      Swal.fire('Error', 'Network error', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  document.title = user
    ? `${user.displayName || user.userName} - Profile`
    : 'User Profile';

  if (loading) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='page-content text-center pt-5'>
            <Spinner color='primary' type='grow' />
            <p className='mt-3 text-muted fw-medium'>Loading user profile...</p>
          </div>
        </Container>
      </div>
    );
  }

  const isActualError = error || (!loading && !user);

  if (isActualError) {
    const isUnauthorized =
      error?.includes('401') ||
      error?.includes('Unauthorized') ||
      error?.includes('Session expired');
    return (
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title={isUnauthorized ? 'Session Expired' : 'Error'}
            pageTitle='User Management'
          />
          <div className='text-center py-5'>
            {isUnauthorized ? (
              <>
                <i className='ri-lock-password-line display-1 text-danger'></i>
                <h4 className='mt-4'>{error || 'Session Expired'}</h4>
                <Button
                  color='primary'
                  className='mt-3'
                  onClick={() => (window.location.href = '/login')}
                >
                  Login Again
                </Button>
              </>
            ) : (
              <>
                <i className='ri-error-warning-line display-1 text-warning'></i>
                <h4 className='mt-4'>User Not Found</h4>
                <Alert color='danger' className='d-inline-block mt-2'>
                  {error || 'User data is missing'}
                </Alert>
                <div className='mt-3'>
                  <Link to='/apps-usermanagement'>
                    <Button color='primary' className='btn-label'>
                      <i className='ri-arrow-left-line label-icon align-middle fs-16 me-2'></i>{' '}
                      Back to List
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb
          title={user?.displayName || user?.userName || 'User Profile'}
          pageTitle='User Management'
        />

        {/* Profile Hero Section */}
        <div className='profile-foreground position-relative mx-n4 mt-n4'>
          <div className='profile-wid-bg'>
            <div className='bg-primary' style={{ height: '300px' }}></div>
          </div>
        </div>

        <div className='pt-4 mb-4 mb-lg-3 pb-lg-4'>
          <Row className='g-3 g-lg-4 align-items-center'>
            <Col xs='auto' className='text-center text-sm-start'>
              <div className='avatar-lg'>
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt='user-img'
                    className='img-thumbnail rounded-circle'
                    width={96}
                    height={96}
                  />
                ) : (
                  <div
                    className='avatar-title rounded-circle bg-light text-primary fs-2 fw-bold'
                    style={{
                      width: '96px',
                      height: '96px',
                      lineHeight: '96px',
                    }}
                  >
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </div>
                )}
              </div>
            </Col>

            <Col xs={12} sm className='text-center text-sm-start'>
              <div className='p-2'>
                <h3 className='text-white mb-2 mb-sm-1'>
                  {user?.displayName || user?.userName}
                </h3>
                <p className='text-white text-opacity-75 mb-2'>
                  {user?.jobTitle || 'No Position'}
                </p>
                <div className='d-flex flex-column flex-sm-row gap-2 gap-sm-3 text-white-50'>
                  <div className='d-flex align-items-center justify-content-center justify-content-sm-start'>
                    <i className='ri-shield-user-line me-1 text-white text-opacity-75 fs-16'></i>
                    <span className='small'>
                      {user?.roles?.join(', ') || 'No Role'}
                    </span>
                  </div>
                  <div className='d-none d-sm-block text-white-50'>|</div>
                  <div className='d-flex align-items-center justify-content-center justify-content-sm-start'>
                    <i className='ri-building-2-line me-1 text-white text-opacity-75 fs-16'></i>
                    <span
                      className='small text-truncate'
                      style={{ maxWidth: '200px' }}
                    >
                      {user?.roles && user.roles.length > 0
                        ? user.roles.some(r => r.toUpperCase() === 'DMUT')
                          ? 'MITRA'
                          : 'PERTAMINA'
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} sm='auto' className='order-sm-last'>
              <div className='d-flex flex-wrap justify-content-center justify-content-sm-end gap-2'>
                {user?.lockoutEnabled &&
                  new Date(user.lockoutEnd || '') > new Date() && (
                    <Badge color='danger' className='px-3 py-2 fs-13'>
                      <i className='ri-lock-2-fill me-1'></i>
                      LOCKED
                    </Badge>
                  )}
              </div>
            </Col>
          </Row>
        </div>

        <Row>
          <Col lg={12}>
            {/* Action Buttons */}
            <div className='d-flex flex-column flex-sm-row gap-2 mb-3'>
              <Link to='/apps-usermanagement/view' className='btn btn-light'>
                <i className='ri-arrow-left-line align-bottom me-1'></i> Back
              </Link>

              <div className='d-none d-lg-flex gap-2 ms-auto'>
                <button
                  className='btn btn-warning'
                  onClick={() => {
                    setFormErrors({});
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                    });
                    setShowPasswordModal(true);
                  }}
                >
                  <i className='ri-key-2-line align-bottom me-1'></i> Change
                  Password
                </button>
                {lockStatus.isLocked ? (
                  <button
                    className='btn btn-success'
                    onClick={() => handleLockUnlock('unlock')}
                    disabled={actionLoading}
                  >
                    <i className='ri-lock-unlock-line align-bottom me-1'></i>{' '}
                    Unlock
                  </button>
                ) : (
                  <button
                    className='btn btn-danger'
                    onClick={() => handleLockUnlock('lock')}
                    disabled={actionLoading}
                  >
                    <i className='ri-lock-2-line align-bottom me-1'></i> Lock
                  </button>
                )}
                <button
                  className='btn btn-success'
                  onClick={() => setShowEditModal(true)}
                >
                  <i className='ri-edit-box-line align-bottom me-1'></i> Edit
                  Profile
                </button>
              </div>

              <UncontrolledDropdown className='d-lg-none ms-auto'>
                <DropdownToggle color='primary' caret>
                  <i className='ri-more-2-fill'></i> Actions
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    onClick={() => {
                      setFormErrors({});
                      setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                      });
                      setShowPasswordModal(true);
                    }}
                  >
                    <i className='ri-key-2-line me-2'></i> Change Password
                  </DropdownItem>
                  <DropdownItem
                    onClick={() =>
                      handleLockUnlock(lockStatus.isLocked ? 'unlock' : 'lock')
                    }
                    disabled={actionLoading}
                  >
                    <i
                      className={`${
                        lockStatus.isLocked
                          ? 'ri-lock-unlock-line'
                          : 'ri-lock-2-line'
                      } me-2`}
                    ></i>
                    {lockStatus.isLocked ? 'Unlock User' : 'Lock User'}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => setShowEditModal(true)}>
                    <i className='ri-edit-box-line me-2'></i> Edit Profile
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>

            <Row>
              {/* Sidebar */}
              <Col xxl={4}>
                {/* Info Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-3'>Info</h5>
                    <div className='table-responsive'>
                      <Table className='table-borderless mb-0'>
                        <tbody>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Username :
                            </th>
                            <td className='text-muted'>{user?.userName}</td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Email :
                            </th>
                            <td className='text-muted'>
                              {user?.email}
                              {user?.emailConfirmed && (
                                <i className='ri-verified-badge-fill text-success ms-1'></i>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Employee No :
                            </th>
                            <td className='text-muted'>
                              {user?.employeeNo || '-'}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Division :
                            </th>
                            <td className='text-muted'>
                              {user?.division || '-'}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Type :
                            </th>
                            <td className='text-muted'>
                              {user?.employeeType || '-'}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>

                {/* Security Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-4'>Security</h5>

                    <div className='mb-3'>
                      <p className='text-muted mb-1'>Failed Attempts</p>
                      <h6 className='mb-0'>{user?.accessFailedCount || 0}</h6>
                    </div>
                    <div>
                      <p className='text-muted mb-1'>Lockout Until</p>
                      <h6 className='mb-0'>
                        {!user?.lockoutEnd ||
                        new Date(user.lockoutEnd) <= new Date()
                          ? 'Not Locked'
                          : new Date(user.lockoutEnd).getFullYear() > 2100
                            ? 'Permanently Locked'
                            : new Date(user.lockoutEnd).toLocaleString()}
                      </h6>
                    </div>
                  </CardBody>
                </Card>

                {/* Audit Trail */}
                <Card>
                  <CardHeader>
                    <h5 className='card-title mb-0'>Audit Trail</h5>
                  </CardHeader>
                  <CardBody>
                    <div className='profile-timeline'>
                      <div
                        className='accordion accordion-flush'
                        id='auditAccordion'
                      >
                        <div className='accordion-item border-0'>
                          <div className='accordion-header'>
                            <button
                              className='accordion-button p-2 shadow-none'
                              type='button'
                            >
                              <div className='d-flex'>
                                <div className='flex-shrink-0 avatar-xs'>
                                  <div className='avatar-title bg-success-subtle text-success rounded-circle'>
                                    <i className='ri-add-circle-line'></i>
                                  </div>
                                </div>
                                <div className='flex-grow-1 ms-3'>
                                  <h6 className='fs-14 mb-1'>
                                    Account Created
                                  </h6>
                                  <small className='text-muted'>
                                    Created on{' '}
                                    {user?.createdDate
                                      ? new Date(
                                          user.createdDate
                                        ).toLocaleString()
                                      : '-'}
                                  </small>
                                </div>
                              </div>
                            </button>
                          </div>
                          <div className='accordion-collapse collapse show'>
                            <div className='accordion-body ms-2 ps-5'>
                              User account was created by{' '}
                              <strong>{user?.createdBy || 'System'}</strong>
                            </div>
                          </div>
                        </div>

                        {user?.updatedDate && (
                          <div className='accordion-item border-0'>
                            <div className='accordion-header'>
                              <button
                                className='accordion-button p-2 shadow-none'
                                type='button'
                              >
                                <div className='d-flex'>
                                  <div className='flex-shrink-0 avatar-xs'>
                                    <div className='avatar-title bg-warning-subtle text-warning rounded-circle'>
                                      <i className='ri-edit-circle-line'></i>
                                    </div>
                                  </div>
                                  <div className='flex-grow-1 ms-3'>
                                    <h6 className='fs-14 mb-1'>Last Updated</h6>
                                    <small className='text-muted'>
                                      Modified on{' '}
                                      {new Date(
                                        user.updatedDate
                                      ).toLocaleString()}
                                    </small>
                                  </div>
                                </div>
                              </button>
                            </div>
                            <div className='accordion-collapse collapse show'>
                              <div className='accordion-body ms-2 ps-5'>
                                Profile was last modified by{' '}
                                <strong>{user?.updatedBy || 'System'}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Main Content */}
              <Col xxl={8}>
                {/* About Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-3'>About</h5>
                    <p>
                      {user?.displayName} is a{' '}
                      {user?.employeeType || 'employee'} working as{' '}
                      {user?.jobTitle || 'staff member'} in{' '}
                      {user?.division || 'the organization'}.
                    </p>
                    <Row>
                      <Col xs={6} md={6}>
                        <div className='d-flex mt-4'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-user-2-fill'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1 overflow-hidden'>
                            <p className='mb-1 text-muted small'>Full Name :</p>
                            <h6 className='text-truncate mb-0 fw-bold'>
                              {user?.displayName}
                            </h6>
                          </div>
                        </div>
                      </Col>
                      <Col xs={6} md={6}>
                        <div className='d-flex mt-4'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-briefcase-line'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1 overflow-hidden'>
                            <p className='mb-1 text-muted small'>
                              Designation :
                            </p>
                            <h6 className='text-truncate mb-0 fw-bold'>
                              {user?.jobTitle || '-'}
                            </h6>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className='d-flex mt-4 border-top pt-3'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-map-pin-line'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1'>
                            <p className='mb-1 text-muted small'>Address :</p>
                            <h6
                              className='mb-0 fw-bold'
                              style={{
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                              }}
                            >
                              {user?.address || '-'}
                            </h6>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Assignments */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-4'>Assignments</h5>
                    <div className='mb-3'>
                      <h6 className='fs-14 mb-2'>Roles</h6>
                      <div className='d-flex flex-wrap gap-2'>
                        {user?.roles && user.roles.length > 0 ? (
                          <div className='d-flex flex-wrap gap-2'>
                            {user.roles.map((role, idx) => (
                              <div
                                key={idx}
                                className='border rounded p-2 d-flex align-items-center bg-light'
                                style={{
                                  minWidth: '250px',
                                  flex: '1 1 calc(50% - 1rem)',
                                }}
                              >
                                <div className='avatar-sm flex-shrink-0'>
                                  <span className='avatar-title bg-primary text-white rounded fs-18'>
                                    <i className='ri-shield-user-line'></i>
                                  </span>
                                </div>
                                <div className='flex-grow-1 ms-3 overflow-hidden'>
                                  <div className='fw-bold fs-13 text-truncate'>
                                    {role}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className='text-center p-3 border border-dashed rounded w-100'>
                            <span className='text-muted fst-italic fs-12'>
                              - No Role Assigned -
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h6 className='fs-14 mb-2'>Positions</h6>
                      <div className='d-flex flex-wrap gap-2'>
                        {user?.userPositions &&
                        user.userPositions.length > 0 ? (
                          user.userPositions.map((pos, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-2 d-flex align-items-center bg-light'
                              style={{
                                minWidth: '250px',
                                flex: '1 1 calc(50% - 1rem)',
                              }}
                            >
                              <div className='avatar-sm flex-shrink-0'>
                                <span className='avatar-title bg-warning text-white rounded fs-18'>
                                  <i className='ri-briefcase-line me-1'></i>
                                </span>
                              </div>
                              <div className='flex-grow-1 ms-3 overflow-hidden'>
                                <div className='fw-bold fs-13 text-truncate'>
                                  {pos.positionName}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className='text-muted fst-italic'>
                            - No Position Assigned -
                          </span>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Change Password Modal */}
        <Modal
          isOpen={showPasswordModal}
          toggle={() => setShowPasswordModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowPasswordModal(false)}>
            Change Password for {user?.userName}
          </ModalHeader>
          <ModalBody>
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleChangePassword();
              }}
            >
              <FormGroup>
                <Label>
                  Current Password <span className='text-danger'>*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword || ''}
                    onChange={e => {
                      setPasswordForm({
                        ...passwordForm,
                        currentPassword: e.target.value,
                      });
                      if (formErrors.currentPassword)
                        setFormErrors({
                          ...formErrors,
                          currentPassword: '',
                        });
                    }}
                    placeholder='Enter current password'
                    invalid={!!formErrors.currentPassword}
                  />
                  <Button
                    color='body'
                    outline
                    className='border'
                    type='button'
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    <i
                      className={`${
                        showCurrentPassword ? 'ri-eye-off-fill' : 'ri-eye-fill'
                      } align-middle`}
                    ></i>
                  </Button>
                  <FormFeedback>{formErrors.currentPassword}</FormFeedback>
                </InputGroup>
              </FormGroup>

              <FormGroup>
                <Label>
                  New Password <span className='text-danger'>*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword || ''}
                    onChange={e => {
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      });
                      if (formErrors.newPassword)
                        setFormErrors({
                          ...formErrors,
                          newPassword: '',
                        });
                    }}
                    placeholder='Min 8 chars, Uppercase & Symbol'
                    invalid={!!formErrors.newPassword}
                  />
                  <Button
                    color='body'
                    outline
                    className='border'
                    type='button'
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    <i
                      className={`${
                        showNewPassword ? 'ri-eye-off-fill' : 'ri-eye-fill'
                      } align-middle`}
                    ></i>
                  </Button>
                  <FormFeedback>{formErrors.newPassword}</FormFeedback>
                </InputGroup>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              onClick={() => setShowPasswordModal(false)}
            >
              Cancel
            </Button>
            <Button
              color='warning'
              onClick={handleChangePassword}
              disabled={actionLoading}
            >
              {actionLoading ? <Spinner size='sm' /> : 'Update Password'}
            </Button>
          </ModalFooter>
        </Modal>

        <UserFormModal
          isOpen={showEditModal}
          toggle={() => {
            setShowEditModal(false);
            fetchUserDetail();
          }}
          userData={user}
          onSuccess={handleEditSuccess}
        />
      </Container>
    </div>
  );
};

export default UserDetailPage;
